/**
 * Spanish copy for the before-and-after patient stories.
 *
 * The photos, slugs and ordering come from `results-stories.ts`; only the
 * human-readable text is translated here, keyed by story slug, so the Spanish
 * gallery, the Spanish story pages and the Spanish share-card generator all
 * read from one source of truth.
 *
 * Patient quotes are translated faithfully; the original English wording stays
 * available on the English pages.
 */

import { esStoryHeadline, esStoryLabel } from "@/lib/story-titles-es";
import { storySlug, stories, type Story } from "@/lib/results-stories";

/** Spanish caption shown under the photos and reused in ImageObject schema. */
const ES_CAPTIONS: Record<string, string> = {
  "paula-c-1":
    "Transformación de pérdida de peso de Paula C., lado a lado, después de su cirugía bariátrica con el Dr. Matthew Fitzer en el norte de Virginia.",
  "an-anonymous-virginia-bariatric-patient-2":
    "Resultado de antes y después de una paciente anónima tras su cirugía bariátrica en Virginia Bariatric Surgery.",
  "an-anonymous-patient-who-got-her-groove-back-3":
    "Una paciente anónima de vuelta en la pista de baile después de su cirugía de pérdida de peso con el Dr. Matthew Fitzer.",
  "an-anonymous-transformation-4":
    "Comparación de antes y después de una paciente anónima de Virginia Bariatric Surgery.",
  "another-amazing-anonymous-transformation-5":
    "Otra comparación de antes y después de una paciente anónima de cirugía bariátrica en el norte de Virginia.",
  "an-anonymous-patient-s-one-year-change-6":
    "El cambio de antes y después de una paciente anónima tras su cirugía de pérdida de peso en Virginia Bariatric Surgery.",
  "m-m-7":
    "M. M. antes y después de su cirugía de pérdida de peso con el Dr. Matthew Fitzer en Virginia Bariatric Surgery.",
  "l-s-8":
    "L. S. fotografiada antes y después de su cirugía bariátrica en el norte de Virginia.",
  "jdb-9":
    "Resultado de pérdida de peso de JDB, antes y después de su cirugía con el Dr. Matthew Fitzer.",
  "jack-a-10":
    "Jack A. antes y después de su proceso de cirugía bariátrica en Virginia Bariatric Surgery.",
  "shelly-t-11":
    "Transformación de pérdida de peso de Shelly T., lado a lado, después de su cirugía con el Dr. Matthew Fitzer.",
  "alfred-and-patricia-crews-12":
    "Alfred y Patricia Crews, una pareja que se operó de pérdida de peso junta en Virginia Bariatric Surgery.",
  "amanda-g-13":
    "Amanda G. muestra su progreso de pérdida de peso después de su cirugía bariátrica en el norte de Virginia.",
  "amanda-g-another-view-14":
    "Amanda G., otra vista de sus resultados de pérdida de peso con el Dr. Matthew Fitzer.",
  "amanda-r-15":
    "Amanda R. antes y casi dos años después de su cirugía de manga gástrica con el Dr. Matthew Fitzer.",
  "ban-h-16":
    "Comparación de antes y después de Ban H. tras su cirugía bariátrica en el norte de Virginia.",
  "chad-beckhert-17":
    "Chad Beckhert un año después de su cirugía de pérdida de peso en Virginia Bariatric Surgery.",
  "cynthia-welgoss-18":
    "Cynthia Welgoss, quien comenzó su proceso quirúrgico de pérdida de peso con un IMC de 35, antes y después.",
  "e-m-and-his-mom-19":
    "E.M. y su mamá, ambos pacientes de cirugía de pérdida de peso del Dr. Matthew Fitzer.",
  "a-halloween-costume-transformation-20":
    "Una paciente de Virginia Bariatric Surgery con disfraz de Halloween, antes y después de su cirugía de pérdida de peso.",
  "jason-lamprey-21":
    "Jason Lamprey antes y después de su cirugía bariátrica con el Dr. Matthew Fitzer.",
  "kristina-and-sean-22":
    "Kristina y Sean, una pareja que se operó de pérdida de peso junta en el norte de Virginia.",
  "lori-m-23":
    "Resultado de pérdida de peso de Lori M., antes y después de su cirugía en Virginia Bariatric Surgery.",
  "lorraine-w-24":
    "Lorraine W. fotografiada antes y después de su cirugía bariátrica con el Dr. Matthew Fitzer.",
  "lucretia-dennington-25":
    "Transformación de pérdida de peso de Lucretia Dennington, lado a lado, en el norte de Virginia.",
  "michael-t-26":
    "Michael T. antes y después de la cirugía que, según él, le cambió la vida.",
  "mr-g-27":
    "El Sr. G después de su cirugía de pérdida de peso en Virginia Bariatric Surgery.",
  "shona-gardner-28":
    "Shona Gardner tres años después de su cirugía de pérdida de peso con el Dr. Matthew Fitzer.",
  "tawana-b-29":
    "Comparación de antes y después de Tawana B. tras su cirugía bariátrica.",
  "tiffany-w-30":
    "Tiffany W. antes y después de su cirugía de pérdida de peso en Virginia Bariatric Surgery.",
  "k-campos-31":
    "K. Campos antes y después de su cirugía de manga gástrica con el Dr. Matthew Fitzer en el norte de Virginia.",
  "k-s-32":
    "Transformación de pérdida de peso de K. S., de cuerpo completo, en Virginia Bariatric Surgery.",
  "an-anonymous-virginia-bariatric-weight-loss-surgery-patient-33":
    "Resultado de antes y después, lado a lado, de un paciente anónimo tras su cirugía bariátrica con el Dr. Matthew Fitzer en el norte de Virginia.",
  "an-anonymous-bariatric-surgery-patient-s-side-by-side-result-34":
    "Resultado de antes y después de un paciente anónimo tras su cirugía bariátrica con el Dr. Matthew Fitzer en Virginia Bariatric Surgery, en el norte de Virginia.",
  "an-anonymous-transformation-35":
    "Transformación de antes y después de una paciente anónima en Virginia Bariatric Surgery.",
};

/** Spanish version of the short taglines that appear above a few photos. */
const ES_TAGLINES: Record<string, string> = {
  "an-anonymous-patient-who-got-her-groove-back-3":
    "¡Hablando de recuperar la energía!",
  "kristina-and-sean-22": "¡Pareja poderosa!",
  "mr-g-27": "Olvidó su foto de antes. Así es la vida…",
};

/** Spanish translations of the patient testimonials. */
const ES_QUOTES: Record<string, string> = {
  "amanda-r-15":
    "¡El Dr. Fitzer y su equipo son FABULOSOS! Me hicieron la manga gástrica hace casi 2 años y nunca me he sentido más saludable. El apoyo antes y después de la cirugía ha sido increíble.",
  "chad-beckhert-17":
    "Buenos días al equipo de Virginia Bariatric: hace un año ustedes me cambiaron la vida para siempre y por eso quiero agradecerle a todo el personal. Desde el primer día que llamé para comenzar este proceso, su consultorio ha sido, sin duda, el mejor con el que he tratado. Ahora estoy en una etapa mucho más disfrutable de mi vida. Les adjunto un par de fotos de antes y después para que las agreguen a su cartelera. De nuevo, gracias por acompañarme en este proceso.",
  "cynthia-welgoss-18":
    "Con un IMC de 35, yo estaba en el límite más bajo de lo que muchos considerarían suficiente para operarse. El Dr. Fitzer tomó en serio mis preocupaciones y me ayudó a encontrar un camino que de verdad funcionó para mí.",
  "jason-lamprey-21":
    "Estoy muy agradecido de haber encontrado a Virginia Bariatric y al Dr. Fitzer. Mi vida cambió para siempre. Todo el equipo me acompañó en cada paso y celebró conmigo cada logro.",
  "michael-t-26":
    "Cuando mi esposa y yo conocimos al Dr. Fitzer, él me preguntó qué diría si me contara que podía cambiarme la vida. Hoy sé exactamente qué decir: gracias.",
  "shona-gardner-28":
    "Hace 3 años el Dr. Fitzer me cambió la vida para bien. Por fin vuelvo a sentirme “yo misma”: con más confianza, más energía y agradecida cada día por la atención que recibí.",
  "k-campos-31":
    "¡Me cambió la vida! Tienen un equipo maravilloso. Temía arrepentirme, pero estoy muy contenta de haberlo hecho. Me siento mucho mejor en todos los sentidos. No me daba cuenta de cuántos de mis problemas de salud estaban relacionados con el peso, porque toda mi vida fui una persona grande. Ahora puedo hacer muchísimas cosas que nunca había podido hacer. ¡Gracias, Dr. Fitzer!",
};

export interface EsStoryCopy {
  slug: string;
  /** Spanish label ("Paula C.", "Una transformación anónima"). */
  label: string;
  /** Spanish headline used as the page H1 and card title. */
  headline: string;
  caption: string;
  tagline?: string;
  quote?: string;
}

/** Spanish copy for one story; falls back to a generic Spanish caption. */
export function esStoryCopy(story: Story, index: number): EsStoryCopy {
  const slug = storySlug(story, index);
  const label = esStoryLabel(slug, story.label);
  return {
    slug,
    label,
    headline: esStoryHeadline(slug, story.label),
    caption:
      ES_CAPTIONS[slug] ??
      `${label}: resultado de antes y después tras la cirugía de pérdida de peso con el Dr. Matthew Fitzer en Virginia Bariatric Surgery.`,
    tagline: ES_TAGLINES[slug],
    quote: ES_QUOTES[slug],
  };
}

/** Spanish copy for every story, in gallery order. */
export const esStories: EsStoryCopy[] = stories.map((s, i) => esStoryCopy(s, i));

/** Look up Spanish copy by slug. */
export function findEsStoryCopy(slug: string): EsStoryCopy | undefined {
  return esStories.find((s) => s.slug === slug);
}

/**
 * Spanish meta title for a story page, kept under ~65 characters.
 * Every candidate carries a brand suffix so the title never matches the H1
 * verbatim (Semrush flags duplicate H1/title).
 */
export function esStoryTitle(copy: EsStoryCopy): string {
  const BRAND = " | Virginia Bariatric";
  const candidates = [
    `${copy.headline}${BRAND}`,
    `${copy.label} — Antes y Después${BRAND}`,
    `${copy.label}${BRAND}`,
  ];
  return (
    candidates.find((c) => c.length <= 72) ??
    `${copy.label.slice(0, 36).replace(/[\s,;:.-]+$/, "")}…${BRAND}`
  );
}


/** Spanish meta description, trimmed on a word boundary. */
export function esStoryDescription(copy: EsStoryCopy, max = 158): string {
  const text = copy.caption;
  if (text.length <= max) return text;
  const cut = text.slice(0, max - 1);
  const space = cut.lastIndexOf(" ");
  return `${(space > max * 0.6 ? cut.slice(0, space) : cut).replace(/[.,;:\s]+$/, "")}…`;
}
